import { baseApi } from 'sama-shared';
import type {
  IStoreConfig,
  IStoreProduct,
  IOrderBudgetPayload,
  ILegacyOrderBudgetResponse,
  ValidateBudgetRequest,
  ValidateBudgetResponse,
  ICreateOrderPayload,
  ICreateOrderResponse,
  IStoreLocation,
  ICustomerInformation,
  ICustomerOrder,
  ICustomerOrderDetail,
  IDeliveryCostResponse,
  IDeliveryCostPayload,
  LocationScheduleResponse,
  LocationIsOpenResponse,
} from '../types/storefront.types';
import type { StorefrontOfferListItem } from '../types/offer.types';
import type { IStoreCombo } from '../types/combo.types';

export type PaymentMethodFlow = 'SAMA_COLLECT' | 'PARTNER_COLLECT';

export interface StorefrontPaymentMethod {
  id: string;
  name: string;
  description: string;
  code?: string | null;
  type?: string;
  instructions?: string | null;
  flowType: PaymentMethodFlow;
  requiresVerification: boolean;
  isActive?: boolean;
}

export interface StorefrontBank {
  id: string;
  name: string;
  code?: string | null;
}

const buildPartnerHeaders = (partnerId: string, locationId?: string) => ({
  'x-partner-id': partnerId,
  ...(locationId ? { 'x-partner-location-id': locationId } : {}),
});

const buildCustomerHeaders = (customerId: string) => ({
  'x-customer-id': customerId,
});

export interface IncomingPaymentTicketRequest {
  partnerId: string;
  locationId: string;
  phone: string;
  amount: number;
}

export interface IncomingPaymentTicketResponse {
  ticket: string;
  expiresIn: number;
}

export interface OrderVerificationTicketRequest {
  partnerId: string;
  locationId: string;
  orderId: string;
}

export interface OrderVerificationTicketResponse {
  ticket: string;
  expiresIn: number;
}

export interface DebitOtpRequest {
  partnerId: string;
  locationId: string;
  bankId: string;
  phone: string;
  nationalId: string;
  /** VES, idéntico a paymentData.amount de la orden. */
  amount: number;
}

export interface DebitOtpResponse {
  success: boolean;
  msgId: string;
  message: string | null;
}

export const storefrontApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getStorefrontConfig: builder.query<IStoreConfig, string>({
      query: (subdomain) => ({
        url: `/partner/storefront/config?subdomain=${encodeURIComponent(subdomain)}`,
        method: 'GET',
      }),
      providesTags: ['StorefrontConfig'],
      keepUnusedDataFor: 60,
    }),

    // --- Schedule: nuevos endpoints públicos (StorefrontPartnerGuard x-partner-id) ---
    getLocationSchedule: builder.query<
      LocationScheduleResponse,
      { locationId: string; partnerId: string }
    >({
      query: ({ locationId, partnerId }) => ({
        url: `/public/location/${locationId}/schedule`,
        method: 'GET',
        headers: buildPartnerHeaders(partnerId),
      }),
      providesTags: (_r, _e, { locationId }) => [
        { type: 'StorefrontConfig', id: `schedule-${locationId}` },
      ],
      keepUnusedDataFor: 5 * 60,
    }),

    getLocationIsOpen: builder.query<
      LocationIsOpenResponse,
      { locationId: string; partnerId: string; at?: string }
    >({
      query: ({ locationId, partnerId, at }) => ({
        url: `/public/location/${locationId}/is-open${at ? `?at=${encodeURIComponent(at)}` : ''}`,
        method: 'GET',
        headers: buildPartnerHeaders(partnerId),
      }),
      providesTags: (_r, _e, { locationId }) => [
        { type: 'StorefrontConfig', id: `isopen-${locationId}` },
      ],
      keepUnusedDataFor: 30,
    }),

    getStorefrontLocations: builder.query<
      IStoreLocation[],
      { partnerId: string; openNow?: boolean }
    >({
      query: ({ partnerId, openNow }) => ({
        url: `/public/location${openNow ? '?openNow=true' : ''}`,
        method: 'GET',
        headers: buildPartnerHeaders(partnerId),
      }),
      providesTags: ['StorefrontConfig'],
      keepUnusedDataFor: 60,
    }),

    checkPartnerLocationCoverage: builder.mutation<
      IStoreLocation,
      { latitude: number; longitude: number; partnerId: string }
    >({
      query: ({ latitude, longitude, partnerId }) => ({
        url: '/public/location/check-coverage',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId),
        body: { latitude, longitude },
      }),
    }),

    getStorefrontProducts: builder.query<
      IStoreProduct[],
      { partnerId: string; locationId: string }
    >({
      query: ({ partnerId, locationId }) => ({
        url: '/public/product',
        method: 'GET',
        headers: buildPartnerHeaders(partnerId, locationId),
      }),
      providesTags: (_result, _error, { locationId }) => [
        { type: 'Product', id: locationId },
        { type: 'Product', id: 'LIST' },
      ],
      keepUnusedDataFor: 2 * 60,
    }),

    getStorefrontProductDetail: builder.query<
      IStoreProduct,
      { productId: string; partnerId: string; locationId: string }
    >({
      query: ({ productId, partnerId, locationId }) => ({
        url: `/public/product/${productId}`,
        method: 'GET',
        headers: buildPartnerHeaders(partnerId, locationId),
      }),
      providesTags: (_result, _error, { productId }) => [{ type: 'Product', id: productId }],
      keepUnusedDataFor: 5 * 60,
    }),

    getActiveOffers: builder.query<
      StorefrontOfferListItem[],
      { partnerId: string; locationId: string }
    >({
      query: ({ partnerId, locationId }) => ({
        url: '/public/offer',
        method: 'GET',
        headers: buildPartnerHeaders(partnerId, locationId),
      }),
      transformResponse: (response: any): StorefrontOfferListItem[] => {
        if (Array.isArray(response)) return response as StorefrontOfferListItem[];
        if (response && Array.isArray(response.data))
          return response.data as StorefrontOfferListItem[];
        return [];
      },
      providesTags: (_r, _e, { locationId }) => [
        { type: 'Offer', id: locationId },
        { type: 'Offer', id: 'LIST' },
      ],
      keepUnusedDataFor: 60,
    }),

    // Solo devuelve promos vendibles en la sucursal; cambia de args al cambiar de sucursal.
    getStorefrontCombos: builder.query<IStoreCombo[], { partnerId: string; locationId: string }>({
      query: ({ partnerId, locationId }) => ({
        url: '/public/combo',
        method: 'GET',
        headers: buildPartnerHeaders(partnerId, locationId),
      }),
      providesTags: ['Combo'],
      keepUnusedDataFor: 60,
    }),

    checkLocationCoverage: builder.mutation<
      IStoreLocation,
      { latitude: number; longitude: number; partnerId: string }
    >({
      query: ({ latitude, longitude, partnerId }) => ({
        url: '/public/location/check-coverage',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId),
        body: { latitude, longitude },
      }),
    }),

    // Introspectivo: el preview de descuentos ya no pasa por aquí; este legacy
    // queda solo para no romper firmas externas hasta removerlo del todo.
    /** @deprecated usar validateOrderBudget + calculateDeliveryCost (total = validate + envío + tip) */
    calculateOrderBudget: builder.mutation<
      ILegacyOrderBudgetResponse,
      { payload: IOrderBudgetPayload; partnerId: string; locationId: string }
    >({
      query: ({ payload, partnerId, locationId }) => ({
        url: '/public/order/budget',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        body: payload,
      }),
      // Normalizar para soportar shape legado y nuevo sin romper consumidores
      transformResponse: (response: any): ILegacyOrderBudgetResponse => {
        if (!response) return response as ILegacyOrderBudgetResponse;
        // Si ya viene con amounts/discounts es el shape nuevo
        if (response.amounts && response.discounts)
          return response as unknown as ILegacyOrderBudgetResponse;
        // Shape viejo: subtotalAmount/totalAmount etc -> mapear a amounts para que FE use budget.amounts
        if (response.subtotalAmount != null || response.totalAmount != null) {
          const ex = response.exchangeRate ?? response.exchangeRateSnapshot ?? 1;
          const cur = response.currencyCode ?? 'VES';
          const sub = Number(response.subtotalAmount ?? 0);
          const del = Number(response.deliveryCost ?? 0);
          const tot = Number(response.totalAmount ?? sub + del);
          const toLocal = (n: number) => (n * ex).toFixed(2);
          return {
            currencyCode: cur,
            exchangeRate: ex,
            amounts: {
              usd: {
                subtotal: sub,
                discountTotal: 0,
                productDiscount: 0,
                orderDiscount: 0,
                deliveryCost: del,
                total: tot,
              },
              local: {
                subtotal: toLocal(sub),
                discountTotal: '0.00',
                productDiscount: '0.00',
                orderDiscount: '0.00',
                deliveryCost: toLocal(del),
                total: toLocal(tot),
              },
            },
            discounts: {
              items: [],
              orderDiscount: null,
              totalSavedUSD: 0,
              totalSavedLocal: '0.00',
            },
            items: [],
            // preservar legacy (sin duplicar currencyCode/exchangeRate)
            subtotalAmount: response.subtotalAmount,
            subtotalAmountConverted: response.subtotalAmountConverted,
            deliveryCost: response.deliveryCost,
            deliveryCostConverted: response.deliveryCostConverted,
            totalAmount: response.totalAmount,
            totalAmountConverted: response.totalAmountConverted,
          } as unknown as ILegacyOrderBudgetResponse;
        }
        return response as ILegacyOrderBudgetResponse;
      },
    }),

    // Introspectivo: validación liviana e idempotente para el carrito; se llama en
    // cada cambio sin pedir dirección ni tipo de entrega.
    validateOrderBudget: builder.mutation<
      ValidateBudgetResponse,
      {
        payload: ValidateBudgetRequest;
        partnerId: string;
        locationId: string;
      }
    >({
      query: ({ payload, partnerId, locationId }) => ({
        url: '/public/order/budget/validate',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        body: payload,
      }),
    }),

    calculateDeliveryCost: builder.mutation<
      IDeliveryCostResponse,
      IDeliveryCostPayload & { partnerId: string; locationId: string }
    >({
      query: ({ latitude, longitude, partnerId, locationId }) => ({
        url: '/public/order/budget/delivery',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        body: { latitude, longitude },
      }),
    }),

    createStorefrontOrder: builder.mutation<
      ICreateOrderResponse,
      { payload: ICreateOrderPayload; partnerId: string; locationId: string }
    >({
      query: ({ payload, partnerId, locationId }) => ({
        url: '/public/order',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        body: payload,
      }),
      invalidatesTags: ['Order', 'CustomerInformation'],
    }),

    getCustomerInformation: builder.query<
      ICustomerInformation,
      { customerId: string; withOrders?: boolean }
    >({
      query: ({ customerId, withOrders }) => ({
        url: '/public/customer',
        method: 'GET',
        headers: buildCustomerHeaders(customerId),
        params: withOrders ? { withOrders: 'true' } : undefined,
      }),
      providesTags: (_result, _error, { customerId }) => [
        { type: 'CustomerInformation', id: customerId },
        { type: 'CustomerInformation', id: 'LIST' },
      ],
      keepUnusedDataFor: 5 * 60,
    }),

    findCustomerByPhone: builder.query<ICustomerInformation, { phone: string; partnerId: string }>({
      query: ({ phone, partnerId }) => ({
        url: '/public/customer/find',
        method: 'GET',
        headers: buildPartnerHeaders(partnerId),
        params: { phone },
      }),
      providesTags: (_result, _error, { phone }) => [
        { type: 'CustomerInformation', id: phone },
        { type: 'CustomerInformation', id: 'LIST' },
      ],
      keepUnusedDataFor: 5 * 60,
    }),

    subscribeToOrderVerification: builder.query<
      { orderId: string; verified: boolean } | null,
      { orderId: string; partnerId: string; locationId: string }
    >({
      queryFn: () => ({ data: null }),

      async onCacheEntryAdded(
        { orderId, partnerId, locationId },
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        const abortController = new AbortController();
        const { signal } = abortController;

        const sseBaseUrl =
          import.meta.env.VITE_PAYMENT_SSE_URL ||
          import.meta.env.VITE_SSE_URL ||
          (import.meta.env.DEV ? 'http://localhost:3001' : '');

        try {
          await cacheDataLoaded;

          const { ticket } = await dispatch(
            storefrontApi.endpoints.requestOrderVerificationTicket.initiate({
              partnerId,
              locationId,
              orderId,
            })
          ).unwrap();

          const response = await fetch(
            `${sseBaseUrl}/event/order/verify?orderId=${encodeURIComponent(orderId)}&ticket=${encodeURIComponent(ticket)}`,
            {
              headers: {
                Accept: 'text/event-stream',
                'Cache-Control': 'no-cache',
              },
              signal,
            }
          );

          if (response.ok && response.body) {
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let currentEventType = 'message';

            while (!signal.aborted) {
              const { value, done } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                const cleanLine = line.trim();
                if (!cleanLine) {
                  currentEventType = 'message';
                  continue;
                }
                if (cleanLine.startsWith('event:')) {
                  currentEventType = cleanLine.substring(6).trim();
                  continue;
                }
                if (cleanLine.startsWith('data:')) {
                  if (currentEventType === 'ping') continue;
                  const dataStr = cleanLine.substring(5).trim();
                  if (dataStr) {
                    try {
                      const parsedData = JSON.parse(dataStr);
                      const payload = parsedData.data ?? parsedData;

                      if (payload && typeof payload === 'object') {
                        updateCachedData(() => ({
                          orderId: payload.orderId,
                          verified: Boolean(payload.verified),
                        }));
                      }
                    } catch {
                      // JSON parse error - silently ignore
                    }
                  }
                }
              }
            }
          }
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') return;
        }

        await cacheEntryRemoved;
        abortController.abort();
      },
    }),

    getMyOrders: builder.query<
      ICustomerOrder[],
      { customerId: string; partnerId: string; partnerLocationId: string }
    >({
      query: ({ customerId, partnerId, partnerLocationId }) => ({
        url: '/public/order',
        method: 'GET',
        headers: {
          ...buildPartnerHeaders(partnerId, partnerLocationId),
          ...buildCustomerHeaders(customerId),
        },
      }),
      providesTags: (_result, _error, { customerId }) => [
        { type: 'CustomerInformation', id: customerId },
        { type: 'CustomerInformation', id: 'LIST' },
      ],
      keepUnusedDataFor: 5 * 60,
    }),

    getMyOrderDetail: builder.query<
      { order: ICustomerOrderDetail; orderShipmentStatusExternal: any },
      { orderId: string; customerId: string; partnerId: string; partnerLocationId: string }
    >({
      query: ({ orderId, customerId, partnerId, partnerLocationId }) => ({
        url: `/public/order/${orderId}`,
        method: 'GET',
        headers: {
          ...buildPartnerHeaders(partnerId, partnerLocationId),
          ...buildCustomerHeaders(customerId),
        },
      }),
      providesTags: (_result, _error, { orderId }) => [
        { type: 'CustomerInformation', id: orderId },
        { type: 'CustomerInformation', id: 'LIST' },
      ],
      keepUnusedDataFor: 5 * 60,
    }),

    getPaymentMethods: builder.query<
      StorefrontPaymentMethod[],
      { partnerId: string; locationId?: string }
    >({
      query: ({ partnerId, locationId }) => ({
        url: '/public/payment/methods',
        method: 'GET',
        headers: buildPartnerHeaders(partnerId, locationId),
      }),
      transformResponse: (response: any): StorefrontPaymentMethod[] => {
        if (Array.isArray(response)) return response as StorefrontPaymentMethod[];
        if (response && Array.isArray(response.data))
          return response.data as StorefrontPaymentMethod[];
        if (response && Array.isArray(response.paymentMethods))
          return response.paymentMethods as StorefrontPaymentMethod[];
        return [];
      },
      providesTags: ['Payment'],
      keepUnusedDataFor: 5 * 60,
    }),

    getStorefrontBanks: builder.query<StorefrontBank[], void>({
      query: () => ({ url: '/public/finance/banks', method: 'GET' }),
      transformResponse: (response: any): StorefrontBank[] => {
        if (Array.isArray(response)) return response;
        if (response && Array.isArray(response.data)) return response.data;
        if (response && Array.isArray(response.banks)) return response.banks;
        return [];
      },
      keepUnusedDataFor: 60 * 60,
    }),

    // --- Ticket SSE público para pagos entrantes (SSE_TICKET_PAGOS_FRONTEND.md §2) ---
    // POST /sse/notification/public/ticket/incoming-payment con x-partner-id +
    // x-partner-location-id. El ticket es de un solo uso y caduca en ~120s: se pide
    // justo antes de abrir el EventSource, nunca se cachea ni se guarda.
    requestIncomingPaymentTicket: builder.mutation<
      IncomingPaymentTicketResponse,
      IncomingPaymentTicketRequest
    >({
      query: ({ partnerId, locationId, phone, amount }) => ({
        url: '/sse/notification/public/ticket/incoming-payment',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        body: { phone, amount },
      }),
    }),

    // --- Débito inmediato (Cobranza Digital): el banco envía la clave (OTP) al cliente ---
    // 429 = ya se pidió una para esa cédula hace < 60 s. La clave va luego en paymentData.otp.
    requestDebitOtp: builder.mutation<DebitOtpResponse, DebitOtpRequest>({
      query: ({ partnerId, locationId, ...body }) => ({
        url: '/public/payment/debit/otp',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        body,
      }),
    }),

    // --- Ticket SSE público para verificación de pago de una orden ---
    // Mismo patrón que requestIncomingPaymentTicket: ticket de un solo uso, se pide
    // justo antes de abrir el EventSource de /event/order/verify.
    requestOrderVerificationTicket: builder.mutation<
      OrderVerificationTicketResponse,
      OrderVerificationTicketRequest
    >({
      query: ({ partnerId, locationId, orderId }) => ({
        url: '/sse/notification/public/ticket/order-verify',
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        body: { orderId },
      }),
    }),

    // --- Comprobante de pago: se sube una vez y la url va en payment.proofUrl de la orden ---
    uploadPaymentProof: builder.mutation<
      { url: string; thumbnailUrl: string },
      { partnerId: string; locationId: string; file: File }
    >({
      query: ({ partnerId, locationId, file }) => {
        const body = new FormData();
        body.append('file', file); // sin Content-Type manual: el navegador agrega el boundary
        return {
          url: '/media/payment-proof/upload',
          method: 'POST',
          headers: buildPartnerHeaders(partnerId, locationId),
          body,
        };
      },
    }),

    // --- Web Push del estado de la orden: idempotente por endpoint (204 sin body) ---
    registerOrderPushSubscription: builder.mutation<
      void,
      { partnerId: string; locationId: string; orderId: string; subscription: PushSubscriptionJSON }
    >({
      query: ({ partnerId, locationId, orderId, subscription }) => ({
        url: `/public/order/${orderId}/push-subscription`,
        method: 'POST',
        headers: buildPartnerHeaders(partnerId, locationId),
        // El DTO usa forbidNonWhitelisted: expirationTime (u otra prop extra) da 400
        body: { endpoint: subscription.endpoint, keys: subscription.keys },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useUploadPaymentProofMutation,
  useGetStorefrontConfigQuery,
  useLazyGetStorefrontConfigQuery,
  useGetLocationScheduleQuery,
  useLazyGetLocationScheduleQuery,
  useGetLocationIsOpenQuery,
  useLazyGetLocationIsOpenQuery,
  useGetStorefrontLocationsQuery,
  useLazyGetStorefrontLocationsQuery,
  useCheckPartnerLocationCoverageMutation,
  useGetStorefrontProductsQuery,
  useLazyGetStorefrontProductsQuery,
  useGetStorefrontProductDetailQuery,
  useGetActiveOffersQuery,
  useGetStorefrontCombosQuery,
  useLazyGetActiveOffersQuery,
  useCheckLocationCoverageMutation,
  useCalculateOrderBudgetMutation,
  useValidateOrderBudgetMutation,
  useCreateStorefrontOrderMutation,
  useGetCustomerInformationQuery,
  useLazyGetCustomerInformationQuery,
  useFindCustomerByPhoneQuery,
  useLazyFindCustomerByPhoneQuery,
  useSubscribeToOrderVerificationQuery,
  useLazySubscribeToOrderVerificationQuery,
  useGetMyOrdersQuery,
  useLazyGetMyOrdersQuery,
  useGetMyOrderDetailQuery,
  useLazyGetMyOrderDetailQuery,
  useCalculateDeliveryCostMutation,
  useGetPaymentMethodsQuery,
  useLazyGetPaymentMethodsQuery,
  useGetStorefrontBanksQuery,
  useRequestIncomingPaymentTicketMutation,
  useRequestOrderVerificationTicketMutation,
  useRequestDebitOtpMutation,
  useRegisterOrderPushSubscriptionMutation,
} = storefrontApi;
